import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { EventDate } from '~/composables/types/event'
import { eventDateGetters } from '~/utils/eventDateGetters'

/**
 * Options for useTicketAvailability composable
 */
export interface TicketAvailabilityOptions {
  /**
   * The selected event date. Can be a ref, computed ref, or plain value.
   * If undefined, all availability values will be 0.
   */
  selectedDate:
    | Ref<EventDate | undefined>
    | ComputedRef<EventDate | undefined>
    | EventDate
    | undefined

  /**
   * Whether the booking is private or public.
   * Private bookings have different min/max ticket requirements.
   * Can be a ref, computed ref, or plain boolean.
   */
  isBookingAsPrivate: Ref<boolean> | ComputedRef<boolean> | boolean

  /**
   * Optional minimum tickets override (e.g., for offers).
   * If provided, this will be used for public bookings instead of the default (1).
   * Can be a ref, computed ref, or plain number.
   */
  minTickets?: Ref<number> | ComputedRef<number | undefined> | number

  /**
   * Optional maximum tickets override (e.g., for offers).
   * If provided, this will be capped against available tickets.
   * Can be a ref, computed ref, or plain number.
   */
  maxTickets?: Ref<number> | ComputedRef<number | undefined> | number
}

/**
 * Return type for useTicketAvailability composable
 */
export interface UseTicketAvailabilityReturn {
  /**
   * Minimum tickets required for private booking (from event date).
   * Returns 0 if no date is selected or date doesn't support private bookings.
   */
  privateMinTickets: ComputedRef<number>

  /**
   * Maximum tickets available for private booking (from event date).
   * Returns 0 if no date is selected or date doesn't support private bookings.
   */
  privateMaxTickets: ComputedRef<number>

  /**
   * Maximum tickets available for public booking (from event date).
   * Returns 0 if no date is selected.
   */
  publicMaxTickets: ComputedRef<number>

  /**
   * Available tickets based on booking type (private or public).
   * Automatically selects privateMaxTickets or publicMaxTickets based on isBookingAsPrivate.
   */
  availableTickets: ComputedRef<number>

  /**
   * Minimum quantity allowed for the current booking type.
   * - For private bookings: returns privateMinTickets
   * - For public bookings: returns minTickets option if provided, otherwise 1
   */
  minQty: ComputedRef<number>

  /**
   * Maximum quantity allowed for the current booking type.
   * - Takes into account maxTickets option if provided
   * - Caps against availableTickets
   * - Returns at least 1 to prevent invalid states
   */
  maxQty: ComputedRef<number>
}

/**
 * Composable for calculating ticket availability (min/max) based on event date and booking type.
 *
 * This composable handles the logic for determining how many tickets can be booked
 * based on whether it's a private or public event, and the selected event date.
 *
 * @param options - Configuration options for ticket availability calculation
 * @returns Object containing computed refs for all availability values
 *
 * @example
 * ```ts
 * const {
 *   minQty,
 *   maxQty,
 *   availableTickets
 * } = useTicketAvailability({
 *   selectedDate: computed(() => props.selectedDate),
 *   isBookingAsPrivate: computed(() => props.isBookingAsPrivate),
 *   minTickets: computed(() => props.minTickets),
 *   maxTickets: computed(() => props.maxTickets),
 * })
 *
 * // Use in template or computed properties
 * const canAddMore = computed(() => quantity.value < maxQty.value)
 * ```
 */
export const useTicketAvailability = (
  options: TicketAvailabilityOptions
): UseTicketAvailabilityReturn => {
  const selectedDate = computed(() => toValue(options.selectedDate))
  const isBookingAsPrivate = computed(() => toValue(options.isBookingAsPrivate))
  const minTickets = computed(() => toValue(options.minTickets))
  const maxTickets = computed(() => toValue(options.maxTickets))

  const privateMinTickets = computed(() =>
    selectedDate.value
      ? eventDateGetters.getPrivateMinTickets(selectedDate.value)
      : 0
  )

  const privateMaxTickets = computed(() =>
    selectedDate.value
      ? eventDateGetters.getPrivateMaxTickets(selectedDate.value)
      : 0
  )

  const publicMaxTickets = computed(() =>
    selectedDate.value
      ? eventDateGetters.getAvailableTicketsQuantity(selectedDate.value)
      : 0
  )

  const availableTickets = computed(() =>
    isBookingAsPrivate.value ? privateMaxTickets.value : publicMaxTickets.value
  )

  const minQty = computed(() => {
    if (isBookingAsPrivate.value) {
      return privateMinTickets.value
    }
    // Use minTickets prop if provided (for offers), otherwise 1
    const minTicketsValue = minTickets.value
    return minTicketsValue && minTicketsValue > 0 ? minTicketsValue : 1
  })

  const maxQty = computed(() => {
    // If maxTickets prop is provided, use it as the maximum
    const maxTicketsValue = maxTickets.value
    // If maxTickets is explicitly provided but invalid (0 or negative), return 1
    if (maxTicketsValue !== undefined && maxTicketsValue <= 0) {
      return 1
    }
    // If maxTickets is provided and valid, use it capped against available tickets
    if (maxTicketsValue !== undefined && maxTicketsValue > 0) {
      return Math.min(maxTicketsValue, availableTickets.value || 1)
    }
    // If maxTickets is not provided, use available tickets (with minimum of 1)
    return availableTickets.value || 1
  })

  return {
    privateMinTickets,
    privateMaxTickets,
    publicMaxTickets,
    availableTickets,
    minQty,
    maxQty,
  } satisfies UseTicketAvailabilityReturn
}
